// 视频生成
import { ModuleId } from "@xmiles/holopix-types";
import { useArtworkStore } from "@/store/artwork";
import { useUserStore } from "@/store/user";
import { useEventTracking } from "@/hooks/useEventTracking";
import { getModuleLabelById, getModuleRouteKeyById } from "@xmiles/holopix-helper";
import { getAivideoGenerateStatus } from "holopix-api";
import { Poller, STATUS, PollResult, PollingError } from "@/utils/QuickTaskPoller";
import { getClientId, initGenerateResult } from "@/hooks/useGenerate";
import { $bus } from "@/mitt";
import { useGeneratingVersions } from "@/hooks/useGeneratingVersions";
import { set, get } from "lodash";
import { useProgress } from "@/hooks/useProgress";
import { message, Modal } from "ant-design-vue";
import { handleEventTracking } from "@/hooks/useEventTracking/eventvalue-util";
import { getConsumeTime } from "@xmiles/holopix-helper";
import { TEAM_QUOTA_EXCEEDED_CODE, TEAM_QUOTA_EXCEEDED_TITLE, TEAM_QUOTA_EXCEEDED_CONTENT, shouldShowTeamQuotaExceededModal } from "@/utils/teamQuotaWarning";
import { h } from "vue";
import SvgIcon from "@/components/SvgIcon/index.vue";

export const module = ModuleId.Aivideo;

// 生图参数
export const aivideoParams = {
  module,
  vipChannel: true,
  apiClientId: "",

  source: 1,
  startTime: 0,
  //没啥用的字段
  prompt: "",
  //火山云视频api视频分辨率
  videoResolution: "720",
  //火山云视频api视频文本
  videoText: "",
  //火山云视频api图片url
  videoImgUrl: "",
  //火山云视频api视频时长
  videoDuration: 5,
  //火山云视频api视频宽高比例
  videoRatio: "adaptive",
  //固定镜头
  videoCamerafixed: false
};

// 将当前用户输入的生图参数转换成后端所需要
export async function getAivideoGenerateParam() {
  const artworkStore = useArtworkStore();
  const userStore = useUserStore();

  const moduleParams = artworkStore.aivideo.params;

  const videoResolution = get(moduleParams, "videoResolution", "720");
  const videoText = get(moduleParams, "videoText", "");
  const videoImgUrl = get(moduleParams, "videoImgUrl", "");
  const videoDuration = get(moduleParams, "videoDuration", 5);
  const videoRatio = get(moduleParams, "videoRatio", "adaptive");
  const videoCamerafixed = get(moduleParams, "videoCamerafixed", false);
  const promptImgId = get(moduleParams, "promptImgId", "");

  const params = {
    module,
    userId: userStore.userInfo.id,
    artworkId: artworkStore.artworkState.id,
    vipChannel: artworkStore.channel === "expedite",
    apiClientId: "",
    source: 1,
    startTime: Date.now(),
    videoResolution,
    videoText,
    prompt: videoText,
    promptImgId,
    videoImgUrl,
    videoDuration,
    videoRatio,
    videoCamerafixed
  };

  return params;
}

const { start, close, onProgressUpdated } = useProgress();

// 开启模块生图
export async function startAivideoGenerate(oldParams?: any) {
  const artworkStore = useArtworkStore();

  const params = oldParams ?? (await getAivideoGenerateParam());
  const module = aivideoParams.module;
  const moduleRouteName = getModuleRouteKeyById(module);
  const clientId = oldParams?.apiClientId || getClientId();

  params.apiClientId = clientId;

  const poller = new Poller({
    interval: 15000,
    shouldLog: true,
    logMessage: `耗时计算-视频生成`
  });

  const { set: setRecord, updateResult, isModuleLatest, del } = useGeneratingVersions();

  const userStore = useUserStore();

  //requestFn — 执行请求的函数
  const requestFn = async (queryParams: any) => {
    try {
      const result = await getAivideoGenerateStatus({ message: JSON.stringify(queryParams) });
      return result;
    } catch (error: any) {
      // holopix-api 使用独立 axios 实例，不经过 responseCodeHandle，需手动处理 90711
      if (error?.code === TEAM_QUOTA_EXCEEDED_CODE) {
        if (shouldShowTeamQuotaExceededModal()) {
          Modal.warning({
            title: TEAM_QUOTA_EXCEEDED_TITLE,
            icon: h(SvgIcon, { directory: "others", name: "tip-modal-icon", size: 24, class: "anticon" }),
            content: TEAM_QUOTA_EXCEEDED_CONTENT,
            okText: "我知道了",
            okType: "primary",
            wrapClassName: "custom-tip-modal",
            bodyStyle: { padding: "16px" },
            centered: true,
          });
        }
        return { status: -1, code: TEAM_QUOTA_EXCEEDED_CODE, message: error.message };
      }
      return {
        status: -1,
        message: error.message
      };
    }
  };

  poller
    .on("complete", async (result: any) => {
      // 通知历史记录侧边栏有新的版本生成
      $bus.emit("hasNewVersion");

      // 该模块最新一次生图
      if (isModuleLatest(clientId)) {
        artworkStore.resetModuleLastVersionParams(module, params);

        // 更新生图面板状态
        set(artworkStore, `${moduleRouteName}.result`, {
          ...get(artworkStore, `${moduleRouteName}.result`),
          code: 3,
          data: {
            status: 3,
            images: [
              {
                id: result.requestId,
                width: 0,
                height: 0,
                image: result.videoUrl,
                clientId: result.requestId,
                versionId: result.versionId
              }
            ],
            param: JSON.stringify(params)
          }
        });

        // 移除该条生图记录
        del(clientId);

        close();
      }

      reportFinishEventTracking({
        result: 1,
        imageIds: result?.data?.imgUrl || result?.videoUrl || ""
      });
    })
    .on("error", (error: any) => {
      console.error("视频生成错误:", error);

      del(clientId);
      artworkStore.setModuleLatest(module);

      // 关闭虚拟进度条
      close();

      // 生图失败、更新用户算力
      userStore.queryUserRights();

      // 90711 已在 requestFn 中弹 Modal，不再重复 toast
      if (error?.code !== TEAM_QUOTA_EXCEEDED_CODE) {
        message.error(error.message || error.msg || "视频生成失败");
      }
      reportFinishEventTracking({
        result: 0
      });
    })
    .poll(
      () => requestFn(params),
      (result: any) => {
        const status = result?.status;

        switch (status) {
          case STATUS.COMPLETED:
            return PollResult.COMPLETE;
          case STATUS.FAILED:
            throw result;
          // return PollResult.CANCEL;
          case STATUS.PROCESSING:
            return PollResult.CONTINUE;
          default:
            throw new PollingError("未知状态");
        }
      }
    );

  close();

  const estimatedTime = getConsumeTime(module, params);
  start(estimatedTime);

  // 更新生图算力
  setTimeout(() => {
    userStore.queryUserRights();
  }, 1500);

  set(artworkStore, `${moduleRouteName}.result`, {
    ...get(artworkStore, `${moduleRouteName}.result`),
    code: 2
  });

  onProgressUpdated((progress) => {
    updateResult(clientId, { progress });
  });

  // 添加生图记录
  setRecord({
    module,
    clientId,
    params,
    result: {
      ...initGenerateResult,
      code: 2,
      progress: 1,
      msg: "正在快速生成中",
      startTime: params.startTime || Date.now()
    },
    socket: undefined,
    cancel: poller.cancel
  });

  /**
   * 火山云api不需要这些参数,人工单独制造一个服务于埋点
   */
  // 人工制造模型数据用于埋点
  const modelName = "火山视频模型";
  const modelId = "volcano_video_model";
  const modelIdList = [modelId];

  // 人工制造模型列表用于埋点事件参数
  const mockModelDetail = {
    id: modelId,
    modelName: modelName,
    userId: 0, // 官方模型
    category: 1
  };
  const modelList = [mockModelDetail];

  const isUserGeneratedText = "否"; // 官方模型，非用户生成

  const eventParms = handleEventTracking(modelList);

  reportStartEventTracking(clientId);

  // 提交生图埋点
  function reportStartEventTracking(clientId: string) {
    const eventValue: any = {
      client_id: clientId,
      module: getModuleLabelById(module),
      source: params.source === 1 ? "直接生成" : "再迭代",
      artwork_id: artworkStore.artworkState.id.toString(),
      prompt: params.prompt,
      hp_model_id: String(modelIdList.join(",")),
      is_team_share_works: artworkStore.artworkState.shareType === 1 ? 1 : 0,
      video_model: modelName,
      video_time: `${params.generateSecond}s`,
      video_pixel: `${params.frameRate}`,
      video_seamless: !!params.isCircleFrameRate,
      hp_is_user_generated: isUserGeneratedText,

      ...eventParms
    };

    useEventTracking({
      event: "creation_event",
      eventValue
    });
  }

  function reportFinishEventTracking({
    client_id = "",
    result = 0 as 0 | 1,
    imageIds = "",
    queue_time_number = 0,
    loading_time_number = 0,
    vipChannel = false
  } = {}) {
    const eventValue: any = {
      client_id,
      module: getModuleLabelById(module),
      source: params.source === 1 ? "直接生成" : "再迭代",
      img_id: imageIds,
      result,
      queue_time_number,
      loading_time_number,
      hp_model_id: String(modelIdList.join(",")),
      hp_model_id_list: modelIdList,
      vipChannel: vipChannel,
      is_team_share_works: artworkStore.artworkState.shareType === 1 ? 1 : 0,
      prompt: params.prompt,
      video_model: modelName,
      video_time: `${params?.generateSecond || params?.videoDuration}s`,
      video_pixel: `${params.frameRate}`,
      video_seamless: !!params.isCircleFrameRate
    };

    useEventTracking({
      event: "artwork_status_event",
      eventValue
    });
  }
}
